/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/projection 层；
 * 2. 负责统一解析特征在设计树与属性投影中的默认显示名称；
 * 3. 该策略层是 cad-core 对外公开的唯一名称语义来源，避免 web-client 再本地硬编码特征名称。
 *
 * 文件职责：
 * 1. 根据特征种类与序号生成默认显示名；
 * 2. 统一处理覆盖名称为空时的兜底命名；
 * 3. 为未来新增 sweep、loft、cut 等特征保留集中扩展点。
 */

/**
 * 特征显示命名上下文。
 *
 * 设计说明：
 * 1. `featureKind` 只能表达“这是圆角”，不能表达 SolidWorks 中固定圆角与变化圆角的不同命名族；
 * 2. 命名策略只消费轻量领域上下文，不直接依赖具体 Prototype 子类，避免投影层与具体实现强耦合；
 * 3. 后续新增同一特征下的子类型命名时，只需要扩展该上下文与策略，不需要在 web-client 重新拼装名称。
 */
export interface FeatureDisplayNameContext {
    /** 圆角类型；变量大小圆角需要使用独立的 SolidWorks 命名族。 */
    filletType?: string | null;
}

/**
 * 特征显示命名族。
 */
export interface FeatureDisplayNameFamily {
    /** 用于同名族独立计数的稳定键。 */
    key: string;
    /** 默认显示名前缀。 */
    prefix: string;
}

/**
 * 特征显示名称策略。
 */
export class FeatureDisplayNamePolicy {
    /**
     * 解析特征显示命名族。
     *
     * @param featureKind 特征种类。
     * @param context 特征显示命名上下文。
     * @returns 显示命名族。
     */
    public resolveFeatureDisplayNameFamily(
        featureKind: string | null | undefined,
        context: FeatureDisplayNameContext = {},
    ): FeatureDisplayNameFamily {
        switch (featureKind) {
            case 'chamfer':
                return { key: 'chamfer', prefix: '倒角' };
            case 'boss-extrude':
                return { key: 'boss-extrude', prefix: '凸台-拉伸' };
            case 'cut-extrude':
                return { key: 'cut-extrude', prefix: '切除-拉伸' };
            case 'fillet':
                return this.resolveFilletDisplayNameFamily(context);
            case 'shell':
                return { key: 'shell', prefix: '抽壳' };
            case 'rib':
                return { key: 'rib', prefix: '筋' };
            case 'boss-revolve':
                return { key: 'boss-revolve', prefix: '凸台-旋转' };
            case 'cut-revolve':
                return { key: 'cut-revolve', prefix: '切除-旋转' };
            default:
                return { key: String(featureKind ?? 'feature'), prefix: '特征' };
        }
    }

    /**
     * 解析特征默认显示名称。
     *
     * @param featureKind 特征种类。
     * @param index 同种特征的显示序号。
     * @param overrideName 外部覆盖名称。
     * @param context 特征显示命名上下文。
     * @returns 最终显示名称。
     */
    public resolveFeatureDisplayName(
        featureKind: string | null | undefined,
        index: number,
        overrideName?: string | null,
        context: FeatureDisplayNameContext = {},
    ): string {
        const trimmedOverride = (overrideName ?? '').trim();
        if (trimmedOverride.length > 0) {
            return trimmedOverride;
        }

        const family = this.resolveFeatureDisplayNameFamily(featureKind, context);
        return `${family.prefix}${index}`;
    }

    /**
     * 解析圆角显示命名族。
     *
     * @param context 特征显示命名上下文。
     * @returns 圆角显示命名族。
     */
    private resolveFilletDisplayNameFamily(context: FeatureDisplayNameContext): FeatureDisplayNameFamily {
        if (context.filletType === 'variable') {
            /**
             * SolidWorks 中变量大小圆角在设计树中使用“变化圆角N”，并且与普通圆角分属不同
             * 命名族；这里将该语义收口在 cad-core，避免 UI 层根据圆角类型自行拼接名称。
             */
            return { key: 'fillet:variable', prefix: '变化圆角' };
        }
        return { key: 'fillet', prefix: '圆角' };
    }
}

/**
 * 默认特征显示名称策略实例。
 */
export const featureDisplayNamePolicy = new FeatureDisplayNamePolicy();
